import { isDefined } from "@clipboard-health/util-ts";
import { logEvent } from "@src/appV2/lib/analytics";

/**
 * Translate legacy facility app routes from Braze payloads to new app routes.
 *
 * The old Flutter-based facility app used different route paths. Notifications
 * sent via Braze may still carry these legacy paths. This mapper translates
 * them to the current app's route structure.
 *
 * Each successful mapping logs an analytics event so we can track usage
 * and determine when to sunset this mapper after Braze migration is complete.
 */

const LEGACY_ROUTE_MAPPED_EVENT = "Workplace App Legacy Route Mapped";

interface MapLegacyRouteOptions {
  isChatRedesignEnabled: boolean;
}

/**
 * Map a legacy route to the current app route.
 * Returns `undefined` if the path is not a known legacy route.
 */
export function mapLegacyRoute(path: string, options: MapLegacyRouteOptions): string | undefined {
  const { isChatRedesignEnabled } = options;
  const [pathname, search] = path.split("?");
  const searchParams = new URLSearchParams(search ?? "");

  // Legacy home route → calendar
  if (pathname === "/") {
    logEvent(LEGACY_ROUTE_MAPPED_EVENT, { from: "/", to: "/calendar" });
    return "/calendar";
  }

  // Legacy worker profile → workplace workers detail
  if (pathname === "/worker-profile") {
    const workerId = searchParams.get("workerId");
    if (isDefined(workerId) && workerId.length > 0) {
      const to = `/workplace/workers/${workerId}`;
      logEvent(LEGACY_ROUTE_MAPPED_EVENT, { from: path, to });
      return to;
    }
  }

  // Legacy chat route → chat route based on feature flag
  if (pathname === "/chat") {
    // Accept both snake_case (legacy Flutter app) and camelCase (Braze campaign URIs)
    const channelUrl = searchParams.get("channel_url") ?? searchParams.get("channelUrl");
    if (isDefined(channelUrl) && channelUrl.length > 0) {
      const parts = channelUrl.split("_");
      if (parts.length === 2 || parts.length === 3) {
        const chatPrefix = isChatRedesignEnabled ? "/chat" : "/facility/chat";
        const to =
          parts.length === 3
            ? `${chatPrefix}/${parts[0]}/${parts[1]}/${parts[2]}`
            : `${chatPrefix}/${parts[0]}/${parts[1]}`;
        logEvent(LEGACY_ROUTE_MAPPED_EVENT, { from: path, to });
        return to;
      }
    }
    // Chat without channel params → chat list
    const chatListPath = isChatRedesignEnabled ? "/chat" : "/facility/chat";
    logEvent(LEGACY_ROUTE_MAPPED_EVENT, { from: path, to: chatListPath });
    return chatListPath;
  }

  // Legacy shift route → shift details
  if (pathname === "/shift") {
    const shiftId = searchParams.get("shiftId");
    if (isDefined(shiftId) && shiftId.length > 0) {
      const to = `/shift-details/${shiftId}`;
      logEvent(LEGACY_ROUTE_MAPPED_EVENT, { from: path, to });
      return to;
    }
  }

  return undefined;
}
